export interface QuadrantAxis {
  /** Label at the low end of the axis. */
  start: string;
  /** Label at the high end. Null when the author gave only one end. */
  end: string | null;
}

export interface QuadrantPoint {
  label: string;
  /** 0–1, left to right. */
  x: number;
  /** 0–1, bottom to top. */
  y: number;
}

/**
 * Mermaid numbers quadrants counter-clockwise from the top-right, so
 * `quadrant-1` is `TopRight` and `quadrant-4` is `BottomRight`. Naming the
 * corners keeps that off-by-one in the parser and out of the renderer.
 */
export enum QuadrantCorner {
  TopRight = 'topRight',
  TopLeft = 'topLeft',
  BottomLeft = 'bottomLeft',
  BottomRight = 'bottomRight',
}

export type QuadrantCorners = Record<QuadrantCorner, string | null>;

export interface ParsedQuadrant {
  title: string | null;
  xAxis: QuadrantAxis | null;
  yAxis: QuadrantAxis | null;
  corners: QuadrantCorners;
  points: QuadrantPoint[];
}

export enum QuadrantLabelAnchor {
  Start = 'start',
  Middle = 'middle',
  End = 'end',
}

export interface QuadrantPlottedPoint {
  label: string;
  /** Stable per-point key — labels repeat only in malformed input. */
  debugIdentity: string;
  centerX: number;
  centerY: number;
  labelX: number;
  labelY: number;
  labelAnchor: QuadrantLabelAnchor;
}

export interface QuadrantCornerLabel {
  text: string;
  x: number;
  y: number;
  anchor: QuadrantLabelAnchor;
}

export interface QuadrantAxisLabel {
  text: string;
  x: number;
  y: number;
  anchor: QuadrantLabelAnchor;
  /** Degrees; the y-axis ends read bottom-to-top in the left margin. */
  rotation: number;
}

export interface QuadrantLayout {
  plotLeft: number;
  plotTop: number;
  plotWidth: number;
  plotHeight: number;
  midX: number;
  midY: number;
  points: QuadrantPlottedPoint[];
  cornerLabels: QuadrantCornerLabel[];
  axisLabels: QuadrantAxisLabel[];
}
